import { useEffect, useRef } from "react";
import { API_URL } from "./api";

type AppointmentChangedEvent = {
  type: "appointment_changed";
};

type AppointmentEventHandler = (payload: AppointmentChangedEvent) => void;

// Subscribes to the SSE stream for a given worker and invokes onEvent on every
// "appointment_changed" message. Returns nothing — the EventSource lifecycle is
// tied to the component mount.
export default function useAppointmentEvents(
  publicId: string | null | undefined,
  onEvent: AppointmentEventHandler,
): void {
  const handlerRef = useRef<AppointmentEventHandler>(onEvent);
  handlerRef.current = onEvent;

  useEffect(() => {
    if (!publicId) return undefined;

    const url = `${API_URL}/api/workers/${publicId}/events`;
    const source = new EventSource(url);

    source.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (payload?.type === "appointment_changed" && handlerRef.current) {
          handlerRef.current(payload);
        }
      } catch {
        // ignore malformed payloads / heartbeat comments
      }
    };

    source.onerror = () => {
      // EventSource auto-reconnects; nothing to do here besides not crashing.
    };

    return () => source.close();
  }, [publicId]);
}
